import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Copy, Lock, Flag, Loader2, CheckCircle2, Clock, Heart } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtNaira } from "@/lib/format";
import { SUGGESTED, friendlyError, deviceId, saveReceipt, receiptsFor, copyText } from "@/lib/askThemes";
import AskCard from "@/components/ask/AskCard";
import PublicLend from "@/components/ask/PublicLend";
import ReportSheet from "@/components/ReportSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";

export function BankBox({ bank }) {
  const copy = async () => { if (await copyText(bank.account_number)) toast.success("Account number copied"); else toast.error("Couldn't copy — please copy the number manually."); };
  return (
    <div data-testid="public-bank-details" className="rounded-2xl border border-border bg-card p-4">
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Send to</p>
      <p className="mt-1 text-base font-semibold">{bank.account_name}</p>
      <p className="text-sm text-muted-foreground">{bank.bank_name}</p>
      <div className="mt-3 flex items-center justify-between rounded-xl bg-secondary px-3 py-2.5">
        <span data-testid="public-account-number" className="font-display text-xl font-extrabold tracking-wider">{bank.account_number}</span>
        <button data-testid="public-copy-account-button" onClick={copy} className="flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground active:scale-95 transition-transform"><Copy className="h-3.5 w-3.5" />Copy</button>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">Transfer from your own banking app. Debii never holds money.</p>
    </div>
  );
}

function MyReceipts({ token, refreshKey }) {
  const [list, setList] = useState([]);
  useEffect(() => {
    const tokens = receiptsFor(token);
    if (tokens.length) api.post("/ask/receipts", { tokens }).then(({ data }) => setList(data.receipts)).catch(() => {});
  }, [token, refreshKey]);
  if (!list.length) return null;
  return (
    <div data-testid="my-receipts" className="space-y-2">
      {list.map((r) => (
        <div key={r.receipt} className="flex items-center justify-between rounded-2xl border border-border bg-card px-4 py-3">
          <span className="text-sm font-semibold">{fmtNaira(r.amount)}</span>
          {r.thanks_path ? (
            <Link data-testid="my-receipt-thanks" to={r.thanks_path} className="flex items-center gap-1 text-xs font-bold text-pink-600"><Heart className="h-3.5 w-3.5" /> Open your Thanks</Link>
          ) : r.status === "confirmed" ? (
            <span className="flex items-center gap-1 text-xs font-bold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Received</span>
          ) : r.status === "rejected" ? (
            <span className="text-xs font-bold text-red-600">Not received yet</span>
          ) : (
            <span data-testid="my-receipt-pending" className="flex items-center gap-1 text-xs font-bold text-amber-700"><Clock className="h-3.5 w-3.5" /> Waiting for confirmation</span>
          )}
        </div>
      ))}
    </div>
  );
}

function SentDrawer({ open, onOpenChange, ask, token, code, onDone }) {
  const [amount, setAmount] = useState(ask.amount ? String(ask.amount) : "");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ask/${token}/contribute`, { amount: Number(amount), name: name || null, device_id: deviceId() }, { headers: code ? { "X-Ask-Code": code } : {} });
      saveReceipt(token, data.receipt);
      onOpenChange(false);
      onDone();
    } catch (e) { const x = apiError(e); toast.error(friendlyError(x)); } finally { setBusy(false); }
  };
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="sent-drawer" className="mx-auto max-w-[30rem] px-5 pb-8 space-y-3">
        <DrawerHeader className="px-0"><DrawerTitle className="font-display text-xl">Tell {ask.requester_name} you sent it</DrawerTitle></DrawerHeader>
        <div className="flex flex-wrap gap-1.5">
          {SUGGESTED.map((s) => (
            <button key={s} data-testid={`suggested-amount-${s}`} onClick={() => setAmount(String(s))} className={`rounded-full px-3.5 py-2 text-xs font-bold transition-all ${Number(amount) === s ? "bg-primary text-primary-foreground scale-105" : "bg-secondary"}`}>{fmtNaira(s)}</button>
          ))}
        </div>
        <Input data-testid="sent-amount-input" inputMode="numeric" placeholder="Amount you sent (₦)" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} className="h-12 rounded-xl font-display text-lg font-bold" />
        <Input data-testid="sent-name-input" placeholder="Your name (optional)" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} className="h-12 rounded-xl" />
        <p className="text-[11px] text-muted-foreground">Your name is only shown to {ask.requester_name}. They'll confirm once it lands in their account.</p>
        <Button data-testid="sent-submit" disabled={busy || !(Number(amount) > 0)} onClick={submit} className="h-12 w-full rounded-xl">{busy ? "Sending…" : "I've sent it"}</Button>
      </DrawerContent>
    </Drawer>
  );
}

export default function PublicAsk() {
  const { token } = useParams();
  const [ask, setAsk] = useState(null);
  const [state, setState] = useState("loading");
  const [code, setCode] = useState(() => sessionStorage.getItem(`ask_code_${token}`) || "");
  const [sentOpen, setSentOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [tick, setTick] = useState(0);

  const load = useCallback(async (c = code) => {
    try {
      const { data } = await api.get(`/ask/${token}`, { headers: c ? { "X-Ask-Code": c } : {} });
      setAsk(data); setState("ok");
      if (c) sessionStorage.setItem(`ask_code_${token}`, c);
    } catch (e) {
      const x = apiError(e);
      if (x === "access_code_required") setState("locked");
      else if (x === "access_code_invalid" || x === "too_many_attempts") { setState("locked"); toast.error(friendlyError(x)); }
      else setState(x === "ask_revoked" ? "revoked" : "invalid");
    }
  }, [token, code]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const shell = (children) => <div className="debii-shell min-h-[100dvh] px-5 pt-6 pb-16">{children}</div>;
  if (state === "loading") return shell(<div className="flex justify-center pt-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>);
  if (state === "invalid" || state === "revoked") return shell(
    <div data-testid={`public-ask-${state}`} className="pt-24 text-center"><p className="font-display text-xl font-bold">{state === "revoked" ? "This link has been turned off" : "This link isn't valid"}</p><p className="mt-2 text-sm text-muted-foreground">Check with the person who sent it.</p></div>);
  if (state === "locked") return shell(
    <div data-testid="public-ask-locked" className="pt-16 text-center">
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary"><Lock className="h-6 w-6 text-primary" /></span>
      <p className="mt-4 font-display text-xl font-bold">Enter the access code</p>
      <p className="mt-1 text-sm text-muted-foreground">The sender shared a 4-digit code with you.</p>
      <Input data-testid="public-ask-code-input" inputMode="numeric" maxLength={4} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="mx-auto mt-5 h-14 w-40 rounded-xl text-center font-display text-2xl tracking-[0.5em]" />
      <Button data-testid="public-ask-code-submit" disabled={code.length !== 4} onClick={() => load(code)} className="mt-4 h-12 w-40 rounded-xl">Open</Button>
    </div>);

  const closed = ask.kind === "gift" && ask.status !== "active";
  return shell(
    <div data-testid="public-ask-page" className="space-y-4">
      <p className="text-center text-xs font-semibold text-muted-foreground">{ask.is_owner ? "Preview — this is what others see" : `${ask.requester_name} sent you a Debii Ask`}</p>
      <AskCard ask={ask} />
      {ask.is_owner && ask.kind === "gift" ? (
        <div data-testid="public-ask-own-request" className="rounded-2xl bg-secondary p-5 text-center">
          <p className="font-display text-lg font-bold">This is your Ask</p>
          <p className="mt-1 text-sm text-muted-foreground">This is how it looks to others.</p>
          <Link data-testid="public-own-manage" to="/asks" className="mt-3 inline-block text-sm font-bold text-accent">Manage in My Asks</Link>
        </div>
      ) : ask.kind === "lend" ? <PublicLend ask={ask} token={token} code={code} onChange={() => load()} /> : closed ? (
        <div data-testid="public-ask-closed" className="rounded-2xl bg-secondary p-5 text-center">
          <p className="font-display text-lg font-bold">{ask.target_reached ? "Target reached 🎉" : "This Ask is closed"}</p>
          <p className="mt-1 text-sm text-muted-foreground">Thank you for checking in.</p>
        </div>
      ) : (
        <>
          {ask.bank && <BankBox bank={ask.bank} />}
          <Button data-testid="public-ive-sent-it-button" onClick={() => setSentOpen(true)} className="h-14 w-full rounded-2xl text-base font-bold">I've sent it 💸</Button>
          <SentDrawer open={sentOpen} onOpenChange={setSentOpen} ask={ask} token={token} code={code} onDone={() => { setTick((t) => t + 1); toast.success(`Thanks! ${ask.requester_name} will confirm once it lands.`); }} />
        </>
      )}
      {ask.kind === "gift" && <MyReceipts token={token} refreshKey={tick} />}
      <div className="flex items-center justify-between pt-4 text-xs">
        {!ask.is_owner ? <button data-testid="public-ask-report" onClick={() => setReportOpen(true)} className="flex items-center gap-1 text-muted-foreground"><Flag className="h-3.5 w-3.5" /> Report</button> : <span />}
        <Link to="/welcome" data-testid="public-ask-made-with" className="font-semibold text-muted-foreground">Made with <span className="font-display font-extrabold text-primary">Debii</span></Link>
      </div>
      <ReportSheet open={reportOpen} onOpenChange={setReportOpen} basePath={`/ask/${token}`} onResolved={(k) => k === "blocked" && load()} />
    </div>
  );
}
